import { useEffect, useState } from 'react';
import { useSkinStyles } from './useSkinStyles';
import { useSkinFeature, SKIN_FEATURE } from '../config/skinRegistry/configs';
import {
  getOddsChangeIndicatorSkinStyles,
  type OddsChangeDirection,
  type OddsChangeIndicatorStyles,
  ODDS_CHANGE_INDICATOR_DURATION,
} from '../styles/components/oddsChangeIndicator';

export type UseOddsChangeIndicatorOptions = {
  canFlash: boolean;
  prevOdds?: number | null;
  changedAt?: number | null;
  currentOdds?: string;
  selected?: boolean;
};

export type OddsChangeIndicatorState = {
  isFlashing: boolean;
  direction: OddsChangeDirection | null;
  indicatorStyles: OddsChangeIndicatorStyles;
};

const getOddsChangeDirection = (
  canFlash: boolean,
  prevOdds?: number | null,
  changedAt?: number | null,
  currentOdds?: string
): OddsChangeDirection | null => {
  const canResolveDirection = canFlash && prevOdds != null && changedAt != null && !!currentOdds;

  if (!canResolveDirection) {
    return null;
  }

  const parsedCurrentOdds = parseFloat(currentOdds);

  if (Number.isNaN(parsedCurrentOdds) || prevOdds === parsedCurrentOdds) {
    return null;
  }

  if (prevOdds < parsedCurrentOdds) {
    return 'up';
  }

  return 'down';
};

export const useOddsChangeIndicator = ({
  canFlash,
  prevOdds,
  changedAt,
  currentOdds,
  selected,
}: UseOddsChangeIndicatorOptions): OddsChangeIndicatorState => {
  const oddsChangeIndicatorEnabled = useSkinFeature(SKIN_FEATURE.ODDS_CHANGE_INDICATOR);
  const activeCanFlash = oddsChangeIndicatorEnabled && canFlash;

  const direction = getOddsChangeDirection(activeCanFlash, prevOdds, changedAt, currentOdds);
  const isFlashing = activeCanFlash && changedAt != null && Date.now() - changedAt < ODDS_CHANGE_INDICATOR_DURATION;

  const [, setTick] = useState(0);

  useEffect(() => {
    if (!changedAt) return;

    const elapsed = Date.now() - changedAt;
    const remaining = ODDS_CHANGE_INDICATOR_DURATION - elapsed;

    if (remaining <= 0) return;

    const timer = setTimeout(() => setTick((t) => t + 1), remaining);

    return () => clearTimeout(timer);
  }, [changedAt]);

  const indicatorStyles = useSkinStyles<OddsChangeIndicatorStyles>(
    getOddsChangeIndicatorSkinStyles(isFlashing, direction ?? 'up', selected)
  );

  return {
    isFlashing,
    direction,
    indicatorStyles,
  };
};
